import { useRef, useState } from 'react';

import { ACCEPTED_UPLOAD_MIME, checkUploadFile, fileToBase64 } from '../../api/client';
import type { FollowupRequest } from '../../api/types';
import { fileSize } from '../../lib/format';

export interface CuratorChatProps {
    /** `permissions.followup`: свои дела — исполнителю, любые — главврачу. */
    canFollowup: boolean;
    pending: boolean;
    busy: boolean;
    onSend: (input: FollowupRequest, fileName: string | null) => Promise<void>;
    onNotice: (message: string) => void;
}

/**
 * Форма хода к ИИ-куратору дела (ТЗ §8.5.1; в макете её нет — разрыв ui-case-card.md §14.1).
 * Старый DOC отклоняется здесь же, до отправки на сервер; PDF, сканы, фото и DOCX читаются на сервере.
 */
export function CuratorChat({ canFollowup, pending, busy, onSend, onNotice }: CuratorChatProps) {
    const [text, setText] = useState('');
    const [forceType, setForceType] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);

    if (!canFollowup) return null;

    function resetFile() {
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
    }

    function pickFile(chosen: File | null) {
        if (!chosen) {
            resetFile();
            return;
        }
        const problem = checkUploadFile(chosen);
        if (problem) {
            onNotice(problem);
            resetFile();
            return;
        }
        setFile(chosen);
    }

    async function submit() {
        const message = text.trim();
        if (!message) {
            onNotice('Сообщение пустое');
            return;
        }
        const input: FollowupRequest = { message };
        if (forceType === 'facts' || forceType === 'question' || forceType === 'new_appeal') {
            input.force_turn_type = forceType;
        }
        if (file) {
            try {
                input.file = await fileToBase64(file);
            } catch {
                onNotice('Не удалось прочитать файл — приложите его ещё раз');
                return;
            }
        }
        try {
            await onSend(input, file ? file.name : null);
            setText('');
            setForceType('');
            resetFile();
        } catch {
            // Текст остаётся в поле, чтобы ход не пропал: тост об ошибке уже показан.
        }
    }

    return (
        <div className="chat-form">
            <textarea
                className="chat-input"
                rows={3}
                value={text}
                disabled={pending}
                placeholder="Вопрос куратору или новая фактура по делу"
                aria-label="Сообщение куратору дела"
                onChange={(event) => setText(event.target.value)}
            />
            <div className="chat-tools">
                <button
                    type="button"
                    className="act-btn primary"
                    disabled={pending || busy || text.trim().length === 0}
                    onClick={() => void submit()}
                >
                    {pending ? 'Куратор отвечает…' : 'Отправить куратору'}
                </button>
                <button type="button" className="act-btn" disabled={pending} onClick={() => fileRef.current?.click()}>
                    📎 Приложить файл
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    hidden
                    accept={ACCEPTED_UPLOAD_MIME}
                    onChange={(event) => {
                        const list = event.target.files;
                        pickFile(list && list.length > 0 ? list[0] : null);
                    }}
                />
                <select
                    className="act-select"
                    aria-label="Тип хода"
                    value={forceType}
                    disabled={pending}
                    onChange={(event) => setForceType(event.target.value)}
                >
                    <option value="">Определить тип автоматически</option>
                    <option value="question">Это вопрос куратору</option>
                    <option value="facts">Это новая фактура по делу</option>
                    <option value="new_appeal">Это другое обращение</option>
                </select>
                {file ? (
                    <span className="chat-file">
                        {'📎 '}
                        <b>{file.name}</b>
                        {' · ' + fileSize(file.size)}
                        <button type="button" aria-label="Убрать вложение" onClick={resetFile}>
                            ✕
                        </button>
                    </span>
                ) : null}
                <span className="chat-note">
                    {file
                        ? 'С вложением ответ куратора готовится дольше: скан или фото распознаётся до нескольких минут.'
                        : 'Ответ куратора — обычно до минуты. Можно приложить PDF, скан, фото, DOCX или текстовый файл.'}
                </span>
            </div>
        </div>
    );
}

export default CuratorChat;
